import React, { useEffect, useRef, useState } from 'react'
import { CircularProgress } from '@mui/material'
import { t } from 'i18next'
import { useDispatch } from 'react-redux'
import { uploadCatalogImageAction } from 'redux/actions/quickListCatalogActions'
import {
  CATALOG_IMAGE_ACCEPT,
  validateCatalogImageFile,
} from '../utils/quickListCatalog.utils'

function CatalogImageInput({
  id,
  label,
  value,
  entityId,
  onChange,
  variant = 'cover',
  hideRemove = false,
  testId,
}) {
  const dispatch = useDispatch()
  const inputRef = useRef(null)
  const [isUploading, setIsUploading] = useState(false)
  const [error, setError] = useState('')
  // La subida puede terminar después de que el campo se desmonte (p. ej. se borró la sección):
  // en ese caso no se toca el estado local, pero onChange se sigue llamando.
  const isMountedRef = useRef(true)

  useEffect(
    () => () => {
      isMountedRef.current = false
    },
    []
  )

  const handleFileChange = async event => {
    const file = event.target.files?.[0]
    event.target.value = ''
    if (!file) return

    const validationError = validateCatalogImageFile(file)
    if (validationError) {
      setError(t(`QUICK_LIST_CATALOG.VALIDATION.${validationError}`))
      return
    }

    setError('')
    setIsUploading(true)

    try {
      const url = await dispatch(uploadCatalogImageAction({ file, entityId }))
      if (url) {
        onChange(url)
      } else if (isMountedRef.current) {
        setError(t('QUICK_LIST_CATALOG.MESSAGE.FAIL_UPLOAD'))
      }
    } catch (uploadError) {
      if (isMountedRef.current)
        setError(t('QUICK_LIST_CATALOG.MESSAGE.FAIL_UPLOAD'))
    } finally {
      if (isMountedRef.current) setIsUploading(false)
    }
  }

  return (
    <div className={`catalog-image-input catalog-image-input--${variant}`}>
      {label && <label htmlFor={id}>{label}</label>}

      <div className="catalog-image-input-preview">
        {isUploading ? (
          <CircularProgress size="2.4rem" />
        ) : value ? (
          <img
            src={value}
            alt={label || ''}
            data-testid={`container-preview-${testId}`}
          />
        ) : (
          <span className="catalog-image-input-empty">
            {t('QUICK_LIST_CATALOG.IMAGE.HELP')}
          </span>
        )}
      </div>

      <div className="catalog-image-input-actions">
        <input
          ref={inputRef}
          id={id}
          type="file"
          accept={CATALOG_IMAGE_ACCEPT}
          className="catalog-hidden-input"
          onChange={handleFileChange}
          data-testid={`input-file-${testId}`}
        />
        <button
          type="button"
          className="btn-add"
          disabled={isUploading}
          onClick={() => inputRef.current?.click()}
          data-testid={`btn-upload-${testId}`}>
          {isUploading
            ? t('QUICK_LIST_CATALOG.IMAGE.UPLOADING')
            : value
            ? t('QUICK_LIST_CATALOG.IMAGE.REPLACE')
            : t('QUICK_LIST_CATALOG.IMAGE.UPLOAD')}
        </button>
        {value && !hideRemove && (
          <button
            type="button"
            className="catalog-link-button"
            disabled={isUploading}
            onClick={() => onChange('')}
            data-testid={`btn-remove-${testId}`}>
            {t('QUICK_LIST_CATALOG.IMAGE.REMOVE')}
          </button>
        )}
      </div>

      {error && (
        <div className="input-feedback">
          <strong data-testid={`text-error-${testId}`}>{error}</strong>
        </div>
      )}
    </div>
  )
}

export default CatalogImageInput
